import React from 'react'
import { useServerCurrency } from '../../lib/hooks/useServerCurrency'
import { useTranslation } from 'next-i18next'

type PriceData = {
  value: number
  currency: string
}

type ProductPrice = {
  value: number
  currency: string
  updated_at?: string
}

type PriceProps = {
  price?: ProductPrice // Main price (usually EUR)
  price_other_currencies?: ProductPrice[] // Other currencies from schema
  prices?: Record<string, PriceData> // Alternative direct price record format
  className?: string
  showFrom?: boolean
  fromText?: string
}

/**
 * Component to display prices in the appropriate currency
 * based on the user's country location and language settings.
 *
 * @example
 * // Sample usage with prepared price object
 * <CurrencyDisplay
 *   prices={{
 *     EUR: { value: 10, currency: 'EUR' },
 *     USD: { value: 11.95, currency: 'USD' },
 *     GBP: { value: 8.50, currency: 'GBP' }
 *   }}
 * />
 *
 * @example
 * // Sample usage with product schema structure
 * <CurrencyDisplay
 *   price={product.price}
 *   price_other_currencies={product.price_other_currencies}
 * />
 */
export const CurrencyDisplay: React.FC<PriceProps> = ({
  price,
  price_other_currencies,
  prices,
  className = '',
  showFrom = false,
  fromText = 'From',
}) => {
  // Get the currency based on user's country
  const { currency } = useServerCurrency()
  const { i18n } = useTranslation()

  // Convert the product price structure to the format we need
  let normalizedPrices: Record<string, PriceData> = {}

  if (prices) {
    // If prices are provided directly, use them
    normalizedPrices = prices
  } else if (price) {
    // If using the product schema structure, normalize it
    normalizedPrices[price.currency] = {
      value: price.value,
      currency: price.currency,
    }

    // Add other currencies if available
    if (price_other_currencies && price_other_currencies.length > 0) {
      price_other_currencies.forEach((otherPrice) => {
        normalizedPrices[otherPrice.currency] = {
          value: otherPrice.value,
          currency: otherPrice.currency,
        }
      })
    }
  }

  // If no prices are available, return null
  if (Object.keys(normalizedPrices).length === 0) return null

  // Find the price in the appropriate currency or fallback to EUR
  const priceData =
    normalizedPrices[currency] ||
    normalizedPrices.EUR ||
    Object.values(normalizedPrices)[0]

  if (!priceData) return null

  // Format the price based on the currency and language
  const formatter = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: priceData.currency,
    minimumFractionDigits: Number.isInteger(priceData.value) ? 0 : 2,
  })
  const formattedPrice = formatter.format(priceData.value)

  return (
    <span className={className}>
      {showFrom && <span className="text-xs font-normal mr-1">{fromText}</span>}
      {formattedPrice}
    </span>
  )
}
